import type {
    ErrorDetailPayload,
    ErrorDetailResponse,
} from '@/types/apps/error-detail.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type ErrorDetailListResponse = ApiResponse<
    PaginatedResult<ErrorDetailResponse>
>;

export class ErrorDetailService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    protected endpoints = {
        base: () => '/api/error-details',
        detail: (id: string) => `/api/error-details/${id}`,
        import: () => '/api/error-details/import',
        export: () => '/api/error-details/export',
    };

    public index(params?: QueryParams): Promise<ErrorDetailListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ErrorDetailResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ErrorDetailPayload
    ): Promise<ApiResponse<ErrorDetailResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: ErrorDetailPayload
    ): Promise<ApiResponse<ErrorDetailResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
